import type { WhoAmIResponse } from '@carculate/api-client';

/**
 * Story `fe-be-monteur-s1-basis` (EPIC-021, S021.1) — de ENIGE plek in de FE die
 * `whoami.fitter` interpreteert. De route-guard, de monteur-schil en hun tests lezen
 * het veld via deze functies, zodat er geen tweede uitleg van "alleen monteur" kan
 * ontstaan.
 *
 * Het veld zelf komt 1-op-1 uit de BE-sessie (`WhoAmIResponse.Fitter`). De BE is
 * autoritatief: het slot zit in de backend (403 `fitter_only_not_allowed`), de FE
 * stuurt alleen de juiste kant op.
 */

/** Het adres van de monteur-weergave (B-1). */
export const FITTER_HOME = '/monteur';

/**
 * Zit deze sessie op slot in de monteur-weergave? Bij een alleen-monteur
 * (`fitterOnly`) én bij een onbekende waarde (`resolved: false`). System krijgt
 * die tweede toestand nooit — dat is een BE-regel (TA-review F-1), dus hier hoeft
 * system niet apart gekend te worden.
 */
export function isFitterOnlyGated(user: WhoAmIResponse | null | undefined): boolean {
    const fitter = user?.fitter;
    if (!fitter) return false;
    return fitter.fitterOnly === true || fitter.resolved === false;
}

/**
 * De staat van de monteur-weergave:
 * - `unresolved` — de monteur-gegevens konden niet worden geladen (Opnieuw proberen);
 * - `not-linked` — geen monteur-koppeling, géén slot (link naar de gewone app);
 * - `orphan` — de laatste koppeling is gewist maar het slot staat (B-7), zonder link;
 * - `fitter` — gekoppeld: de schil met Werk en Meer.
 */
export type FitterShellState = 'unresolved' | 'not-linked' | 'orphan' | 'fitter';

export function fitterShellState(user: WhoAmIResponse | null | undefined): FitterShellState {
    const fitter = user?.fitter;
    if (!fitter) return 'not-linked';
    if (fitter.resolved === false) return 'unresolved';
    if (fitter.isFitter === true) return 'fitter';
    return fitter.fitterOnly === true ? 'orphan' : 'not-linked';
}

/**
 * Mag deze gebruiker vanuit de monteur-weergave naar de volledige app? Alleen wie
 * NIET op slot zit — anders zou de link via de guard teruglussen naar `/monteur`.
 */
export function mayOpenFullApp(user: WhoAmIResponse | null | undefined): boolean {
    return !isFitterOnlyGated(user);
}

/** S3 (AC 28): de supportschermen van de monteur-app (Nederlandse monteur-URL's). */
export const FITTER_SUPPORT_HOME = `${FITTER_HOME}/meer/support`;

/**
 * Story `be-fe-support-soorten-foutmelding-monteur` (S3), AC 36 — waar een alleen-monteur heen gaat die een
 * kantoor-adres opent. De link in de supportmail en het belletje wijzen naar `/support/requests/{nr}`; die komt op het
 * monteur-detail. `/support/requests` komt op de monteur-lijst. Al het andere op `/monteur`.
 */
export function fitterRedirectFor(pathname: string): string {
    const m = /^\/support\/requests(?:\/(\d{1,9}))?\/?$/.exec(pathname);
    if (!m) return FITTER_HOME;
    return m[1] ? `${FITTER_SUPPORT_HOME}/${m[1]}` : FITTER_SUPPORT_HOME;
}

/** Een item in de balk onderin, met z'n eigen zichtbaarheidsregel (geen losse `if` per item). */
export interface FitterNavItem {
    /**
     * Engelse discriminator (S021.2, deel C-1 — CLAUDE.md "identifiers Engels"). Het
     * schermlabel en de route blijven Nederlands ("Werk" → `/monteur`, "Meer" → `/monteur/meer`).
     */
    key: 'work' | 'agenda' | 'more';
    label: string;
    to: string;
    /** Exact-match voor de actieve markering (Werk is de index-route). */
    end: boolean;
    visible: (user: WhoAmIResponse | null | undefined) => boolean;
}

/**
 * De balk onderin (concept: Werk · Agenda · Meer). Agenda was in S021.1 verborgen en is
 * sinds S021.5a zichtbaar. Elk item wordt een echte `<Link>` (CLAUDE.md "Navigatie-items
 * zijn echte links"). De balk zelf staat alleen in de toestand `fitter`, dus
 * "niet gekoppeld" en "niet geladen" zien hem niet.
 */
export const FITTER_NAV_ITEMS: readonly FitterNavItem[] = [
    { key: 'work', label: 'Werk', to: FITTER_HOME, end: true, visible: () => true },
    { key: 'agenda', label: 'Agenda', to: `${FITTER_HOME}/agenda`, end: false, visible: () => true },
    { key: 'more', label: 'Meer', to: `${FITTER_HOME}/meer`, end: false, visible: () => true },
];

/**
 * Story `fe-be-monteur-s5a-agenda-en-meer` (S021.5a, TA-review A-6) — krijgt deze
 * gebruiker op het gewone adres de tip "Op je telefoon werkt de monteur-app beter"?
 * Alleen wie monteur ís (PB-6), met een bekende waarde, en niet op slot (een
 * alleen-monteur wordt al doorgestuurd; zijn kantoorschil is hooguit even zichtbaar).
 * De schermbreedte en het wegklikken toetst de tip zelf.
 */
export function shouldOfferFitterAppTip(user: WhoAmIResponse | null | undefined): boolean {
    const fitter = user?.fitter;
    if (!fitter) return false;
    return fitter.isFitter === true && fitter.resolved !== false && !isFitterOnlyGated(user);
}

/** De zichtbare items voor deze gebruiker. */
export function visibleFitterNavItems(user: WhoAmIResponse | null | undefined): FitterNavItem[] {
    return FITTER_NAV_ITEMS.filter((item) => item.visible(user));
}
